import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { tradeLicenseApi } from '../../services/tradeLicenseApi';
import './CertificateView.css';

const HINDI_MONTHS = [
  'जनवरी', 'फरवरी', 'मार्च', 'अप्रैल', 'मई', 'जून',
  'जुलाई', 'अगस्त', 'सितंबर', 'अक्टूबर', 'नवंबर', 'दिसंबर',
];

/**
 * Professional Municipal Trade License Certificate
 * Works for both Admin (adminMode=true) and Public views.
 */
const CertificateView = ({ adminMode = false }) => {
  const { license_number } = useParams();
  const navigate = useNavigate();
  const [license, setLicense] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const cardRef = useRef(null);

  const fetchLicense = useCallback(async () => {
    try {
      setLoading(true);
      const response = await tradeLicenseApi.lookupLicense(license_number);
      setLicense(response.data);
    } catch (err) {
      console.error(err);
      setError('Certificate not found or invalid license number.');
    } finally {
      setLoading(false);
    }
  }, [license_number]);

  useEffect(() => {
    if (license_number) fetchLicense();
  }, [license_number, fetchLicense]);

  useEffect(() => {
    const fitCertificateToPage = () => {
      const card = cardRef.current;
      if (!card) return;

      const pxPerMm = 96 / 25.4;
      const pageW = 210 * pxPerMm;
      const pageH = 297 * pxPerMm;

      card.style.transform = 'none';
      card.style.width = '794px';
      card.style.height = 'auto';

      const naturalW = card.offsetWidth;
      const naturalH = card.offsetHeight;
      const scaleX = pageW / naturalW;
      const scaleY = pageH / naturalH;

      card.style.transformOrigin = 'top left';
      card.style.transform = `scale(${scaleX}, ${scaleY})`;
      card.style.width = `${naturalW}px`;
      card.style.height = `${naturalH}px`;
    };

    const resetCertificateScale = () => {
      const card = cardRef.current;
      if (!card) return;
      card.style.transform = '';
      card.style.width = '';
      card.style.height = '';
      card.style.transformOrigin = '';
    };

    window.addEventListener('beforeprint', fitCertificateToPage);
    window.addEventListener('afterprint', resetCertificateScale);
    return () => {
      window.removeEventListener('beforeprint', fitCertificateToPage);
      window.removeEventListener('afterprint', resetCertificateScale);
    };
  }, [license]);

  const handlePrint = () => window.print();

  const handleBack = () => {
    if (window.history.length > 1) navigate(-1);
    else adminMode ? navigate('/office/licenses') : navigate('/trade-license');
  };

  /* ── Helpers ── */
  const fmt = (iso) =>
    iso
      ? new Date(iso).toLocaleDateString('en-IN', { day: '2-digit', month: 'long', year: 'numeric' })
      : '—';

  const fmtShort = (iso) =>
    iso ? new Date(iso).toLocaleDateString('en-GB') : '—';

  const fmtHindi = (iso) => {
    if (!iso) return '—';
    const d = new Date(iso);
    return `${d.getDate()} ${HINDI_MONTHS[d.getMonth()]} ${d.getFullYear()}`;
  };

  /* ── Loading / Error ── */
  if (loading)
    return (
      <div className="cert-page">
        <div className="cert-loading">⏳ Loading certificate…</div>
      </div>
    );

  if (error || !license)
    return (
      <div className="cert-page">
        <div className="cert-error">❌ {error || 'Certificate not found.'}</div>
      </div>
    );

  const site = license.site_info || {};
  const certData = license.certificate_data || {};
  const payment = license.payment_info || null;
  const cfg = license.certificate_config || {};
  const customStmts = cfg.statements || [];

  const municipality = site.site_name || 'Nagar Parishad';
  const municipalityHi = site.site_name_hi || municipality;
  const cityName = municipality
    .replace(/nagar (parishad|nigam|panchayat)/i, '')
    .replace(/municipal (corporation|council)/i, '')
    .trim() || municipality.split(' ')[0];
  const cityNameHi = municipalityHi
    .replace(/नगर (परिषद|निगम|पंचायत)/, '')
    .trim() || cityName;
  const websiteUrl = site.website_url || '';

  /* Governing act citation — configurable from office side, Bihar default */
  const actNameEn = cfg.act_name_en || 'Bihar Municipal Act, 2007';
  const actNameHi = cfg.act_name_hi || 'बिहार नगरपालिका अधिनियम, 2007';

  const certNo = certData.certificate_number || license.license_number;
  const financialYear = (() => {
    const date = license.valid_from ? new Date(license.valid_from) : new Date();
    const year = date.getMonth() >= 3 ? date.getFullYear() : date.getFullYear() - 1;
    return `${year}-${year + 1}`;
  })();
  const primaryColor = site.header_bg_color || '#0f2c5c';

  const fee = parseFloat(license.license_fee || 0).toLocaleString('en-IN');
  const feeWordsEn = license.license_fee_words || '';
  const feeWordsHi = license.license_fee_words_hi || '';

  const ownerNameHi = license.owner_name_hi || license.owner_name;
  const businessNameHi = license.business_name_hi || license.business_name;
  const addressHi = license.business_address_hi || license.business_address;
  const fatherName = license.father_husband_name || '';
  const fatherNameHi = license.father_husband_name_hi || fatherName;
  const businessTypeEn = license.trade_category_name || license.business_type || '';
  const businessTypeHi = license.trade_category_name_hi || businessTypeEn;

  const salutationHi =
    license.applicant_gender === 'male' ? 'श्री'
      : license.applicant_gender === 'female' ? 'श्रीमती'
        : 'श्री/श्रीमती';
  const salutationEn =
    license.applicant_gender === 'male' ? 'Mr.'
      : license.applicant_gender === 'female' ? 'Mrs.'
        : 'Mr./Mrs.';

  /* Residence fragments (only rendered when data exists) */
  const residenceHi = [
    license.ward_number ? `वार्ड संख्या ${license.ward_number}` : '',
    license.holding_number ? `होल्डिंग संख्या ${license.holding_number}` : '',
    addressHi || '',
  ].filter(Boolean).join(', ');

  const residenceEn = [
    license.ward_number ? `Ward No. ${license.ward_number}` : '',
    license.holding_number ? `Holding No. ${license.holding_number}` : '',
    license.business_address || '',
  ].filter(Boolean).join(', ');

  return (
    <div className="cert-page" style={{ '--cert-primary': primaryColor }}>

      {/* Action bar */}
      <div className="cert-actions-bar">
        <button className="btn-back" onClick={handleBack}>← Back</button>
        <button className="btn-print" onClick={handlePrint}>🖨️ Print / Download PDF</button>
      </div>

      <div className="certificate-card trade-design-card" ref={cardRef}>
        <div className="cert-corner cert-corner-tl" />
        <div className="cert-corner cert-corner-tr" />
        <div className="cert-corner cert-corner-bl" />
        <div className="cert-corner cert-corner-br" />

        <div className="trade-cert-header">
          <div className="swachh-text hindi">
            {cfg.slogan_hi_line1 || `स्वच्छ ${cityNameHi}`}<br />
            {cfg.slogan_hi_line2 || `सुंदर ${cityNameHi}`}
          </div>
          <div className="trade-logo-wrap">
            {site.logo_url ? <img src={site.logo_url} alt="Municipal Logo" /> : <div className="trade-logo-fallback">नगर परिषद</div>}
          </div>
          <div className="swachh-text right">
            {cfg.slogan_en_line1 || `Swachh ${cityName}`}<br />
            {cfg.slogan_en_line2 || `Sundar ${cityName}`}
          </div>
        </div>

        <div className="trade-title-block">
          <h1>{municipalityHi}</h1>
          <h2>{municipality.toUpperCase()}</h2>
          <div className="trade-title-ribbon">TRADE LICENSE CERTIFICATE</div>
          <h3>व्यापार अनुज्ञप्ति प्रमाण पत्र</h3>
          <p>(Under Section 342 of the {actNameEn})<br />({actNameHi} की धारा 342 के अंतर्गत)</p>
        </div>

        <div className="trade-side-row">
          <div className="trade-qr-box">
            {license.qr_code_url ? <img src={license.qr_code_url} alt="Verification QR" /> : <div>QR</div>}
            <span>Scan to Verify<br />सत्यापित करने हेतु स्कैन करें</span>
          </div>
          <div className="trade-photo-box">
            {license.applicant_photograph_url ? <img src={license.applicant_photograph_url} alt="Applicant" /> : <div className="photo-placeholder">PHOTO</div>}
            <span>PHOTO</span>
          </div>
        </div>

        <div className="trade-meta-strip">
          <div>
            <span className="meta-icon">⚙</span>
            <span>License No.<small>लाइसेंस संख्या</small></span>
            <strong>{license.license_number}</strong>
          </div>
          <div>
            <span className="meta-icon">▤</span>
            <span>Application No.<small>आवेदन संख्या</small></span>
            <strong>{license.application_number || '—'}</strong>
          </div>
          <div>
            <span className="meta-icon">▦</span>
            <span>Issue Date<small>निर्गमन तिथि</small></span>
            <strong>{fmtShort(license.issued_date)}</strong>
          </div>
          <div>
            <span className="meta-icon">▣</span>
            <span>Valid Till<small>वैधता तिथि</small></span>
            <strong>{fmtShort(license.valid_till)}</strong>
          </div>
          <div>
            <span className="meta-icon">▰</span>
            <span>Financial Year<small>वित्तीय वर्ष</small></span>
            <strong>{financialYear}</strong>
          </div>
        </div>

        <div className="trade-cert-body">
          {/* 1 — Legal powers */}
          <div className="trade-rule-block">
            {customStmts[0] ? (
              <>
                {customStmts[0].hi && <p className="hindi">{customStmts[0].hi}</p>}
                {customStmts[0].en && <p>{customStmts[0].en}</p>}
              </>
            ) : (
              <>
                <p className="hindi">
                  {actNameHi} की धारा 129(ख), 129(ग), धारा 421 सहपठित धारा 342 तथा {municipalityHi} व्यापार
                  अनुज्ञप्ति विनियम के अंतर्गत प्रदत्त शक्तियों का प्रयोग करते हुए यह व्यापार अनुज्ञप्ति प्रदान की जाती है।
                </p>
                <p>
                  In exercise of the powers conferred under Section 129(b), 129(c), Section 421 read with Section 342 of
                  the {actNameEn} and the {municipality} Trade License Regulations, this Trade License is hereby granted.
                </p>
              </>
            )}
          </div>

          {/* 2 — Licensee, residence and establishment */}
          <div className="trade-rule-block">
            {customStmts[1] ? (
              <>
                {customStmts[1].hi && <p className="hindi">{customStmts[1].hi}</p>}
                {customStmts[1].en && <p>{customStmts[1].en}</p>}
              </>
            ) : (
              <>
                <p className="hindi">
                  यह अनुज्ञप्ति {salutationHi} <strong>{ownerNameHi}</strong>
                  {fatherNameHi && <span>, पिता/पति/निदेशक <strong>{fatherNameHi}</strong></span>}
                  {residenceHi && <span>, निवासी {residenceHi}</span>}
                  {' '}को उनके प्रतिष्ठान <strong>&ldquo;{businessNameHi}&rdquo;</strong>
                  {businessTypeHi && <span> (व्यवसाय का प्रकार – <strong>{businessTypeHi}</strong>)</span>}
                  {' '}के संचालन हेतु प्रदान की जाती है।
                </p>
                <p>
                  This License is issued to {salutationEn} <strong>{license.owner_name}</strong>
                  {fatherName && <span>, Father/Husband/Director <strong>{fatherName}</strong></span>}
                  {residenceEn && <span>, resident of {residenceEn}</span>}
                  , for carrying on business under the trade name <strong>&ldquo;{license.business_name}&rdquo;</strong>
                  {businessTypeEn && <span> (Business Type – <strong>{businessTypeEn}</strong>)</span>}.
                </p>
              </>
            )}
          </div>

          {/* 3 — Fee, receipt and payment date */}
          <div className="trade-rule-block">
            {customStmts[2] ? (
              <>
                {customStmts[2].hi && <p className="hindi">{customStmts[2].hi}</p>}
                {customStmts[2].en && <p>{customStmts[2].en}</p>}
              </>
            ) : (
              <>
                <p className="hindi">
                  वित्तीय वर्ष {financialYear} हेतु ₹ <strong>{fee}/-</strong>
                  {feeWordsHi && <span> ({feeWordsHi})</span>} का अनुज्ञप्ति शुल्क
                  {payment?.paid_date && <span> दिनांक <strong>{fmtShort(payment.paid_date)}</strong> को</span>}
                  {payment?.receipt_number && <span> रसीद संख्या <strong>{payment.receipt_number}</strong> के माध्यम से</span>}
                  {' '}जमा किया गया है।
                </p>
                <p>
                  A Trade License Fee of ₹ <strong>{fee}/-</strong>
                  {feeWordsEn && <span> ({feeWordsEn})</span>} has been deposited
                  {payment?.paid_date && <span> on <strong>{fmtShort(payment.paid_date)}</strong></span>}
                  {payment?.receipt_number && <span> vide Receipt No. <strong>{payment.receipt_number}</strong></span>}
                  {' '}for the Financial Year {financialYear}.
                </p>
              </>
            )}
          </div>

          {/* 4 — Validity */}
          <div className="trade-rule-block">
            {customStmts[3] ? (
              <>
                {customStmts[3].hi && <p className="hindi">{customStmts[3].hi}</p>}
                {customStmts[3].en && <p>{customStmts[3].en}</p>}
              </>
            ) : (
              <>
                <p className="hindi">
                  यह अनुज्ञप्ति दिनांक <strong>{fmtHindi(license.valid_till)}</strong> तक वैध रहेगी तथा {actNameHi} एवं
                  {' '}{municipalityHi} द्वारा निर्धारित नियमों एवं शर्तों के अधीन होगी।
                </p>
                <p>
                  This License shall remain valid up to <strong>{fmt(license.valid_till)}</strong> and shall be governed by
                  the provisions of the {actNameEn} and the rules and regulations prescribed by {municipality}.
                </p>
              </>
            )}
          </div>
        </div>

        <div className="terms-box">
          <h4>नियम एवं शर्तें / TERMS &amp; CONDITIONS</h4>
          <div className="terms-grid">
            <p><strong>1</strong> यह अनुज्ञप्ति केवल व्यापारिक प्रयोजन हेतु जारी की गई है।<br />This license is issued only for trade purposes.</p>
            <p><strong>7</strong> नवीनीकरण में विलंब होने पर नियमानुसार दंड लगाया जा सकता है।<br />Penalty may be imposed in case of delay in renewal.</p>
            <p><strong>2</strong> सक्षम प्राधिकारी द्वारा किसी भी समय परिसर का निरीक्षण किया जा सकता है।<br />The premises may be inspected by the competent authority at any time.</p>
            <p><strong>8</strong> यह अनुज्ञप्ति केवल {municipalityHi} क्षेत्राधिकार में ही मान्य होगी।<br />This license is valid only within the jurisdiction of {municipality}.</p>
            <p><strong>3</strong> व्यवसाय के प्रकार में किसी भी परिवर्तन हेतु पूर्व अनुमति आवश्यक होगी।<br />Any change in the nature of business shall require prior approval.</p>
            <p><strong>9</strong> परिषद समय-समय पर आवश्यक दिशा-निर्देश जारी कर सकती है।<br />The licensing authority may issue instructions/guidelines from time to time.</p>
            <p><strong>4</strong> अनुज्ञप्ति को प्रतिष्ठान में प्रमुख स्थान पर प्रदर्शित करना अनिवार्य होगा।<br />The license shall be displayed at a prominent place in the premises.</p>
            <p><strong>10</strong> जनहित में परिषद अनुज्ञप्ति को निलंबित अथवा निरस्त करने का अधिकार सुरक्षित रखती है।<br />The authority reserves the right to suspend or cancel/revoke the license in public interest.</p>
            <p><strong>5</strong> स्वामित्व, पते अथवा व्यवसाय की संरचना में परिवर्तन होने पर तुरंत सूचित करें।<br />Any change in ownership, address or constitution of business must be informed immediately.</p>
            <p><strong>11</strong> अवैध निर्माण अथवा अधिनियम के उल्लंघन की स्थिति में अनुज्ञप्ति अमान्य होगी।<br />This license is invalid if the building structure is unauthorizedly constructed or in violation of provisions of the Act/Rules.</p>
            <p><strong>6</strong> यह अनुज्ञप्ति प्रत्येक वित्तीय वर्ष की 31 मार्च को समाप्त हो जाएगी तथा समय पर नवीनीकरण कराना आवश्यक होगा।<br />This license shall expire on 31st March of every financial year and must be renewed before expiry.</p>
            <p><strong>12</strong> यदि अनुज्ञप्ति असत्य सूचना या धोखाधड़ी के आधार पर प्राप्त की गई हो तो यह रद्द कर दी जाएगी।<br />This license is deemed to be cancelled if obtained by any fraud or misrepresentation.</p>
          </div>
        </div>

        <div className="trade-footer">
          <div className="trade-contact">
            {websiteUrl && (
              <div>अधिक जानकारी के लिए वेबसाइट देखें :<br />{websiteUrl}</div>
            )}
            {site.contact_phone && <div>संपर्क / Call us : {site.contact_phone}</div>}
            {site.contact_email && <div>ईमेल / Email : {site.contact_email}</div>}
          </div>
          {cfg.seal_url ? (
            <img className="cert-seal-img" src={cfg.seal_url} alt="Official Seal" />
          ) : (
            <div className="cert-seal"><div className="cert-seal-inner">{municipality}<br />SEAL</div></div>
          )}
          <div className="cert-sig">
            {cfg.signature_url ? (
              <img className="sig-image" src={cfg.signature_url} alt="Signature" />
            ) : (
              <div className="sig-line" />
            )}
            <div className="sig-name">{cfg.signatory_title_hi || 'कार्यपालक पदाधिकारी'}</div>
            <div className="sig-title">{cfg.signatory_title_en || 'Executive Officer'}</div>
            <div className="sig-title">{municipality}</div>
            <div className="sig-title hindi">{municipalityHi}</div>
          </div>
          <div className="trade-govt-logo">
            {cfg.govt_logo_url ? (
              <img src={cfg.govt_logo_url} alt={cfg.govt_logo_caption_en || 'Government Emblem'} />
            ) : (
              <div className="govt-logo-fallback">{cfg.govt_logo_caption_hi || 'बिहार सरकार'}</div>
            )}
            <div className="govt-caption">{cfg.govt_logo_caption_hi || 'बिहार सरकार'}</div>
          </div>
        </div>

        <div className="cert-verify-row">
          * यह कम्प्यूटर जनित दस्तावेज है, इस पर हस्ताक्षर की आवश्यकता नहीं है।<br />
          * This is a computer generated document, does not require any signature. Certificate No: <strong>{certNo}</strong>
        </div>
      </div>
    </div>
  );
};

export default CertificateView;
